import * as z from "zod";

const MAX_LATERAL_DISTANCE_METERS = 10_000;
const DEFAULT_OPACITY = 0.4;

export const XdaProjectionColorEntrySchema = z.object({
  uwi: z.string().min(1, "UWI cannot be empty"),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Color must be a valid hex color"),
  group: z.string()
});

export const XdaProjectionRequestSchema = z.object({
  uwis: z.array(z.string().min(1)).min(1, "At least one UWI is required"),
  leftLateralDistance: z
    .number()
    .nonnegative("Left lateral distance cannot be negative")
    .max(
      MAX_LATERAL_DISTANCE_METERS,
      `Left lateral distance exceeds maximum of ${MAX_LATERAL_DISTANCE_METERS} meters`
    )
    .finite("Left lateral distance must be a finite number"),
  rightLateralDistance: z
    .number()
    .nonnegative("Right lateral distance cannot be negative")
    .max(
      MAX_LATERAL_DISTANCE_METERS,
      `Right lateral distance exceeds maximum of ${MAX_LATERAL_DISTANCE_METERS} meters`
    )
    .finite("Right lateral distance must be a finite number"),
  colorMap: z.array(XdaProjectionColorEntrySchema).optional()
});

export const XdaProjectionFeaturePropertiesSchema = z.object({
  uwi: z.string(),
  color: z.string(),
  group: z.string().optional(),
  opacity: z.number().default(DEFAULT_OPACITY)
});

const PositionSchema = z.array(z.number()).min(2).max(3);

const PolygonCoordinatesSchema = z.array(z.array(PositionSchema));

export const PolygonGeometrySchema = z.object({
  type: z.literal("Polygon"),
  coordinates: PolygonCoordinatesSchema
});

export const MultiPolygonGeometrySchema = z.object({
  type: z.literal("MultiPolygon"),
  coordinates: z.array(PolygonCoordinatesSchema)
});

export const XdaProjectionFeatureSchema = z.object({
  type: z.literal("Feature"),
  geometry: z.union([PolygonGeometrySchema, MultiPolygonGeometrySchema]),
  properties: XdaProjectionFeaturePropertiesSchema
});

export const XdaProjectionResponseSchema = z.object({
  type: z.literal("FeatureCollection"),
  features: z.array(XdaProjectionFeatureSchema)
});

export type XdaProjectionColorEntry = z.infer<typeof XdaProjectionColorEntrySchema>;
export type XdaProjectionRequest = z.infer<typeof XdaProjectionRequestSchema>;
export type XdaProjectionFeatureProperties = z.infer<
  typeof XdaProjectionFeaturePropertiesSchema
>;
export type XdaProjectionFeature = z.infer<typeof XdaProjectionFeatureSchema>;
export type XdaProjectionResponse = z.infer<typeof XdaProjectionResponseSchema>;
